/* eslint-disable jsx-a11y/anchor-is-valid */

import { Body, Box } from '@hover/blueprint';
import { dArrowRight, Link } from '@hover/blueprint/brand';
import {
  chakra,
  LinkBox,
  LinkOverlay as ChakraLinkOverlay,
  LinkOverlayProps as ChakraLinkOverlayProps,
} from '@hover/blueprint/chakra';
import { Link as GatsbyLink } from 'gatsby';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { IHomePageBetterForEveryone } from '../../../interfaces/IHomePageBetterForEveryone';

interface Props {
  card: IHomePageBetterForEveryone;
}

type LinkOverlayComponent = FunctionComponent<ChakraLinkOverlayProps>;

const BetterForEveryoneCard: React.FC<Props> = ({ card }) => {
  const to = process.env.GATSBY_V2_PREFIX + card.linkTo;

  const NativeLinkOverlay: LinkOverlayComponent = useCallback(
    props => <ChakraLinkOverlay {...props} href={to} />,
    [to],
  );

  const GatsbyLinkOverlay: LinkOverlayComponent = useCallback(
    props => <ChakraLinkOverlay {...props} to={to} as={GatsbyLink} />,
    [to],
  );

  const LinkOverlay = card.nativeLink ? NativeLinkOverlay : GatsbyLinkOverlay;

  const [isOver, setIsOver] = useState(false);

  return (
    <LinkBox
      cursor="pointer"
      flexDir="column"
      _hover={{
        transform: { desktop: 'scale(1.03)' },
        shadow: { base: 'none', desktop: 'distance300' },
      }}
      transition="all 0.1s ease-in"
      borderTopRadius={500}
      borderBottomRadius={200}
      overflow="hidden"
      onMouseEnter={() => setIsOver(true)}
      onMouseLeave={() => setIsOver(false)}
    >
      <chakra.div pointerEvents="none">{card.image}</chakra.div>
      <Box
        flexDir="column"
        pl={{ base: 0, desktop: 400 }}
        pr={{ base: 0, desktop: 200 }}
        pb={{ base: 0, desktop: 500 }}
      >
        <Body
          as="h2"
          mt={600}
          letterSpacing="0.5px"
          color={card.titleColor}
          isBold
          size={300}
          textTransform="uppercase"
        >
          <LinkOverlay>{card.title}</LinkOverlay>
        </Body>
        <Body mt={400} pb={100} size={500}>
          {card.description}
        </Body>
        <Link
          as={GatsbyLink}
          to={to}
          mt={600}
          d="flex"
          alignItems="center"
          decorationAfter={dArrowRight}
          isOver={isOver}
        >
          <chakra.span
            d={{ base: 'flex', tablet: 'none' }}
            color="primary.600"
            isSemiBold
            mr={200}
            size={500}
          >
            {card.responsiveLinkTitle ?? card.linkTitle}
          </chakra.span>
          <chakra.span
            id="better-for-everyone-card-title"
            d={{ base: 'none', tablet: 'flex' }}
            color="primary.600"
            isSemiBold
            mr={200}
            size={500}
          >
            {card.linkTitle}
          </chakra.span>
        </Link>
      </Box>
    </LinkBox>
  );
};

export default BetterForEveryoneCard;
